<template>
	<ion-modal
		:is-open="isOpen"
		class="note-observations-modal"
		@did-dismiss="handleDidDismiss"
	>
		<ion-header>
			<ion-toolbar color="branding">
				<ion-title>{{ t("note.select-observations") }}</ion-title>
				<ion-buttons slot="end">
					<ion-button @click="closeModal">
						<ion-icon :icon="closeOutline" />
					</ion-button>
				</ion-buttons>
			</ion-toolbar>
		</ion-header>

		<ion-content class="ion-padding">
			<!-- Loading state -->
			<div v-if="!categories" class="space-y-4">
				<div
					v-for="i in 5"
					:key="i"
					class="h-16 animate-pulse rounded-xl bg-gray-200"
				></div>
			</div>

			<!-- Categories accordion -->
			<ion-accordion-group
				v-else-if="categories && categories.length > 0"
				class="space-y-4"
			>
				<ion-accordion
					v-for="category in categories"
					:key="category.id"
					:value="category.id.toString()"
					class="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm"
				>
					<!-- Category header -->
					<div slot="header" class="p-0">
						<div class="bg-gray-50 px-6 py-4">
							<div class="flex items-center justify-between">
								<div class="flex items-center space-x-3">
									<i
										:class="category.icon"
										class="text-xl text-gray-600"
									/>
									<div>
										<h3
											class="m-0 text-lg font-semibold text-gray-800"
										>
											{{ category.name }}
										</h3>
										<p
											v-if="
												getSelectedCountForCategory(
													category.id,
												) > 0
											"
											class="m-0 min-h-[1.25rem] text-sm font-medium text-blue-600"
										>
											<span>
												{{
													getSelectedCountForCategory(
														category.id,
													)
												}}
												{{ t("common.selected") }}
											</span>
										</p>
									</div>
								</div>
								<div class="flex items-center space-x-2">
									<ion-icon
										:icon="chevronDownOutline"
										class="text-xl text-gray-400"
									/>
								</div>
							</div>
						</div>
					</div>

					<!-- Subcategories content -->
					<div slot="content" class="p-0">
						<div
							v-if="
								category.subcategories &&
								category.subcategories.length > 0
							"
							class="divide-y divide-gray-100"
						>
							<div
								v-for="subcategory in category.subcategories"
								:key="subcategory.id"
								class="transition-colors"
								:class="{
									'border-l-4 border-blue-500 bg-blue-50':
										isSubcategorySelected(
											category.id,
											subcategory.id,
										),
								}"
							>
								<!-- Subcategory checkbox -->
								<div
									class="cursor-pointer px-6 py-4 hover:bg-gray-50"
									@click="
										toggleSubcategory(category, subcategory)
									"
								>
									<div class="flex items-center space-x-4">
										<ion-checkbox
											:checked="
												isSubcategorySelected(
													category.id,
													subcategory.id,
												)
											"
											@ion-change="
												toggleSubcategory(
													category,
													subcategory,
												)
											"
											@click.stop
										/>
										<span
											class="flex-1 text-base"
											:class="{
												'font-medium text-gray-900':
													isSubcategorySelected(
														category.id,
														subcategory.id,
													),
												'text-gray-700':
													!isSubcategorySelected(
														category.id,
														subcategory.id,
													),
											}"
										>
											{{ subcategory.name }}
										</span>
									</div>
								</div>

								<!-- Severity slider (appears when selected) -->
								<div
									v-if="
										isSubcategorySelected(
											category.id,
											subcategory.id,
										)
									"
									class="bg-blue-50 px-6 pb-4"
									@click.stop
								>
									<div class="ml-7">
										<SeveritySlider
											:model-value="
												getSubcategorySeverity(
													category.id,
													subcategory.id,
												)
											"
											@update:model-value="
												updateSeverity(
													category.id,
													subcategory.id,
													$event,
												)
											"
										/>
									</div>
								</div>
							</div>
						</div>

						<!-- Empty state for category -->
						<div v-else class="px-6 py-8 text-center">
							<p class="m-0 text-gray-500">
								{{ t("note.no-subcategories-available") }}
							</p>
						</div>
					</div>
				</ion-accordion>
			</ion-accordion-group>

			<!-- No categories message -->
			<div
				v-else
				class="flex flex-col items-center justify-center py-16 text-center"
			>
				<i class="fas fa-seedling mb-4 text-5xl text-gray-300" />
				<h3 class="mb-2 text-xl font-semibold text-gray-500">
					{{ t("note.no-observation-categories") }}
				</h3>
				<p class="m-0 text-gray-400">
					{{ t("note.categories-will-appear") }}
				</p>
			</div>
		</ion-content>

		<!-- Footer with actions -->
		<ion-footer>
			<ion-toolbar>
				<ion-button
					slot="start"
					fill="clear"
					color="danger"
					:disabled="!modelValue || modelValue.length === 0"
					@click="clearAllSelections"
				>
					{{ t("common.clear-all") }}
				</ion-button>
				<ion-button
					slot="end"
					:disabled="!modelValue || modelValue.length === 0"
					@click="closeModal"
				>
					{{ t("common.done")
					}}{{
						modelValue && modelValue.length > 0
							? ` (${modelValue.length})`
							: ""
					}}
				</ion-button>
			</ion-toolbar>
		</ion-footer>
	</ion-modal>
</template>

<script setup lang="ts">
	import { ref } from "vue";
	import {
		IonModal,
		IonHeader,
		IonToolbar,
		IonTitle,
		IonButtons,
		IonButton,
		IonContent,
		IonAccordionGroup,
		IonAccordion,
		IonCheckbox,
		IonFooter,
		IonIcon,
	} from "@ionic/vue";
	import { closeOutline, chevronDownOutline } from "ionicons/icons";
	import { useNoteObservationCategories } from "seedgreen-shared/queries/note-observation";
	import {
		NoteObservationCategoryDTO,
		NoteObservationSubcategoryDTO,
		NoteObservationDTO,
	} from "seedgreen-shared/dtos/NoteObservationDTO";
	import SeveritySlider from "@/components/feature/notes/SeveritySlider.vue";
	import { useI18n } from "vue-i18n";

	const { t } = useI18n();

	const model = defineModel<NoteObservationDTO[]>({
		default: () => [],
	});

	const { categories } = useNoteObservationCategories();

	const isOpen = ref(false);

	// Computed properties
	const getSelectedCountForCategory = (categoryId: number): number => {
		if (!model.value) return 0;
		return model.value.filter(
			(selection) => selection.categoryId === categoryId,
		).length;
	};

	const getSubcategorySeverity = (
		categoryId: number,
		subcategoryId: number,
	): number => {
		if (!model.value) return 5;
		const selection = model.value.find(
			(s) =>
				s.categoryId === categoryId &&
				s.subcategoryId === subcategoryId,
		);
		return selection?.severity || 5;
	};

	const isSubcategorySelected = (
		categoryId: number,
		subcategoryId: number,
	): boolean => {
		if (!model.value) return false;
		return model.value.some(
			(selection) =>
				selection.categoryId === categoryId &&
				selection.subcategoryId === subcategoryId,
		);
	};

	// Methods
	const openModal = (): void => {
		isOpen.value = true;
	};

	const closeModal = (): void => {
		isOpen.value = false;
	};

	const handleDidDismiss = (): void => {
		isOpen.value = false;
	};

	const updateSeverity = (
		categoryId: number,
		subcategoryId: number,
		severity: number,
	): void => {
		if (!model.value) return;

		model.value = model.value.map((selection) => {
			if (
				selection.categoryId === categoryId &&
				selection.subcategoryId === subcategoryId
			) {
				return { ...selection, severity };
			}
			return selection;
		});
	};

	const toggleSubcategory = (
		category: NoteObservationCategoryDTO,
		subcategory: NoteObservationSubcategoryDTO,
	): void => {
		const currentValue = model.value || [];
		const isSelected = isSubcategorySelected(category.id, subcategory.id);

		if (isSelected) {
			// Remove selection
			model.value = currentValue.filter(
				(selection) =>
					!(
						selection.categoryId === category.id &&
						selection.subcategoryId === subcategory.id
					),
			);
		} else {
			// Add selection with default severity
			const newSelection: NoteObservationDTO = {
				categoryId: category.id,
				subcategoryId: subcategory.id,
				categoryName: category.name,
				subcategoryName: subcategory.name,
				categoryIcon: category.icon ?? null,
				severity: 5, // Default to medium severity
			};
			model.value = [...currentValue, newSelection];
		}
	};

	const clearAllSelections = (): void => {
		model.value = [];
	};

	defineExpose({ openModal, closeModal });
</script>

<style scoped>
	/* Desktop styles */
	@media (min-width: 768px) {
		.note-observations-modal {
			--height: 90%;
		}
	}
</style>
